/**
 * Date utility functions using date-fns for consistent date formatting
 */
import { format, parseISO, isValid } from 'date-fns';

/**
 * Safely parse a date string or Date object
 * @param {string|Date} date - Date string or Date object
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export const parseDate = (date) => {
  if (!date) return null;
  
  if (date instanceof Date) {
    return isValid(date) ? date : null;
  }
  
  try {
    // Handle ISO strings
    if (typeof date === 'string') {
      const parsed = parseISO(date);
      return isValid(parsed) ? parsed : null;
    }
  } catch {
    return null;
  }
  
  return null;
};

/**
 * Format date for display (e.g., "Jan 15, 2026")
 * @param {string|Date} date - Date to format
 * @param {string} formatStr - date-fns format string (default: 'MMM d, yyyy')
 * @returns {string} - Formatted date string or fallback
 */
export const formatDate = (date, formatStr = 'MMM d, yyyy') => {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  
  try {
    return format(parsed, formatStr);
  } catch {
    return '—';
  }
};

/**
 * Format date for API/form inputs (e.g., "2026-01-15")
 * @param {string|Date} date - Date to format
 * @returns {string} - ISO date string or empty string
 */
export const formatDateISO = (date) => {
  const parsed = parseDate(date);
  if (!parsed) return '';
  
  try {
    return format(parsed, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Format date range for display
 * @param {string|Date} start - Start date
 * @param {string|Date} end - End date
 * @param {string} formatStr - date-fns format string
 * @returns {string} - Formatted range string
 */
export const formatDateRange = (start, end, formatStr = 'MMM d, yyyy') => {
  const startFormatted = formatDate(start, formatStr);
  const endFormatted = formatDate(end, formatStr);
  
  if (startFormatted === '—' && endFormatted === '—') return '—';
  if (startFormatted === '—') return endFormatted;
  if (endFormatted === '—') return startFormatted;
  
  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Format relative date (e.g., "Jan 15" without year for current year)
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateShort = (date) => {
  return formatDate(date, 'MMM d');
};

/**
 * Format full date with time
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (date) => {
  return formatDate(date, 'MMM d, yyyy h:mm a');
};
